import * as Tabs from '@radix-ui/react-tabs';
import { Plus, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import type { StickerRef } from '@/data/db';
import { Dialog } from '@/components/primitives/dialog';
import { TextInput } from '@/components/primitives/field';
import { cn } from '@/lib/cn';
import {
  EMOJI_CHOICES,
  STICKER_LIBRARY,
  Sticker,
  emojiSticker,
  stickerName,
  svgSticker,
} from './sticker-library';

interface StickerPickerProps {
  value: readonly StickerRef[];
  onChange: (stickers: StickerRef[]) => void;
  /** 最近使用，來自 settings.recentStickers。 */
  recent?: readonly StickerRef[];
  onUse?: (sticker: StickerRef) => void;
}

/** 建議上限 5 個；超過時卡片正面只顯示前 3 個並以 +N 表示（spec 21.7）。 */
export const STICKER_SUGGESTED_MAX = 5;

export function StickerPicker({ value, onChange, recent = [], onUse }: StickerPickerProps) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const svgMatches = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    if (!needle) return STICKER_LIBRARY;
    return STICKER_LIBRARY.filter(
      (sticker) =>
        sticker.name.includes(needle) ||
        sticker.keywords.some((word) => word.toLowerCase().includes(needle)),
    );
  }, [keyword]);

  function toggle(sticker: StickerRef) {
    const next = value.includes(sticker)
      ? value.filter((current) => current !== sticker)
      : [...value, sticker];
    onChange(next);
    if (!value.includes(sticker)) onUse?.(sticker);
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[0.8125rem] font-medium text-text-muted">
          貼紙
          {value.length > STICKER_SUGGESTED_MAX && (
            <span className="ml-2 text-[0.75rem]">
              超過 {STICKER_SUGGESTED_MAX} 個，卡片正面只會顯示前 3 個
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.8125rem] font-medium text-text-muted hover:bg-accent-soft hover:text-text"
        >
          <Plus size={14} />
          選貼紙
        </button>
      </div>

      {value.length === 0 ? (
        <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
          貼紙純粹是裝飾與分類用，也可以拿來搜尋。
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((sticker) => (
            <li key={sticker}>
              <button
                type="button"
                onClick={() => toggle(sticker)}
                aria-label={`移除貼紙 ${stickerName(sticker)}`}
                className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1.5 hover:border-danger"
              >
                <Sticker sticker={sticker} size={16} />
                <X size={11} className="text-text-muted group-hover:text-danger" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="選貼紙"
        description="圖案是內建的原創插圖，Emoji 由系統字體顯示。兩種都可以拿來搜尋與篩選。"
        size="md"
      >
        <div className="space-y-4">
          <TextInput
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜尋貼紙"
            aria-label="搜尋貼紙"
          />

          {recent.length > 0 && (
            <section className="space-y-1.5">
              <h4 className="text-[0.75rem] font-medium text-text-muted">最近使用</h4>
              <StickerGrid stickers={[...recent]} selected={value} onPick={toggle} />
            </section>
          )}

          <Tabs.Root defaultValue="svg">
            <Tabs.List className="mb-3 inline-flex rounded-full bg-surface-sunken p-1">
              {[
                { value: 'svg', label: '圖案' },
                { value: 'emoji', label: 'Emoji' },
              ].map((tab) => (
                <Tabs.Trigger
                  key={tab.value}
                  value={tab.value}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-[0.8125rem] text-text-muted',
                    'data-[state=active]:bg-surface-raised data-[state=active]:font-medium',
                    'data-[state=active]:text-text data-[state=active]:shadow-[var(--shadow-ambient)]',
                  )}
                >
                  {tab.label}
                </Tabs.Trigger>
              ))}
            </Tabs.List>

            <Tabs.Content value="svg">
              {svgMatches.length === 0 ? (
                <p className="text-[0.8125rem] text-text-muted">沒有符合的圖案。</p>
              ) : (
                <StickerGrid
                  stickers={svgMatches.map((sticker) => svgSticker(sticker.key))}
                  selected={value}
                  onPick={toggle}
                />
              )}
            </Tabs.Content>

            <Tabs.Content value="emoji">
              <StickerGrid
                stickers={EMOJI_CHOICES.map(emojiSticker)}
                selected={value}
                onPick={toggle}
              />
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </Dialog>
    </section>
  );
}

function StickerGrid({
  stickers,
  selected,
  onPick,
}: {
  stickers: StickerRef[];
  selected: readonly StickerRef[];
  onPick: (sticker: StickerRef) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {stickers.map((sticker) => (
        <li key={sticker}>
          <button
            type="button"
            onClick={() => onPick(sticker)}
            aria-pressed={selected.includes(sticker)}
            aria-label={stickerName(sticker)}
            className={cn(
              'grid size-10 place-items-center rounded-[var(--radius-control)] border',
              'transition-colors duration-200',
              selected.includes(sticker)
                ? 'border-accent bg-accent-soft'
                : 'border-border hover:border-border-strong hover:bg-surface-sunken',
            )}
          >
            <Sticker sticker={sticker} size={20} />
          </button>
        </li>
      ))}
    </ul>
  );
}
